import { Modal, Descriptions, Tag, Divider, Typography, Card, Space, Spin } from 'antd';
import { formatEndTime, formatUnixTime } from '../../utils/entity-utils';
import { ServiceCodeMaintain, ScopeMaintain, mapMantenanceScope } from '../../assets/constants/constants';
import { useEffect, useState } from 'react';
import {
    apiGetListPackageDataSponsorPartner,
    apiGetListPackageDataCodesPartner,
    apiGetListPackageDataSponsor,
    apiGetListPackageDataCodes
} from '../../api';

const { Title, Text } = Typography;

const serviceCodeLabels: Record<ServiceCodeMaintain, string> = {
    [ServiceCodeMaintain.TOP_UP]: 'Top up',
    [ServiceCodeMaintain.PIN_CODE]: 'Pin code',
    [ServiceCodeMaintain.DATA_CODE]: 'Data code',
    [ServiceCodeMaintain.DATA_SPONSOR]: 'Data sponsor',
    [ServiceCodeMaintain.TKG]: 'TKG'
};

interface ServiceMaintain {
    serviceCode: ServiceCodeMaintain;
    providerNames?: string[];
}

interface ServiceMaintainEntity {
    serviceCode: ServiceCodeMaintain;
    dataPacks?: string[];
}

interface PartnerMaintain {
    cpCode: string;
    infoMaintain?: ServiceMaintainEntity[];
}

interface DataPackMaintainEntity {
    id: string;
    providerNames?: string[];
}

interface PackageMaintain {
    serviceCode: ServiceCodeMaintain;
    dataPacks?: DataPackMaintainEntity[];
}

interface MaintainScheduler {
    id: string;
    name: string;
    startTime: string;
    expireTime: number;
    note?: string;
    createdAt: string;
    createdBy: string;
    scopeType: ScopeMaintain;
    serviceMaintains?: ServiceMaintain[];
    partnerMaintains?: PartnerMaintain[];
    packageMaintains?: PackageMaintain[];
}

interface ModalViewMaintenanceSchedulerProps {
    open: boolean;
    onClose: () => void;
    data: MaintainScheduler | null;
}

export default function ModalViewMaintenanceScheduler({
    open,
    onClose,
    data
}: ModalViewMaintenanceSchedulerProps) {
    const [packageDetailsMap, setPackageDetailsMap] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!data || !open) {
            setPackageDetailsMap({});
            return;
        }

        const fetchPackageDetails = async () => {
            setLoading(true);
            try {
                const newPackageDetailsMap: Record<string, any> = {};

                // For PARTNER scope - fetch partner packages
                if (data.scopeType === ScopeMaintain.PARTNER && data.partnerMaintains) {
                    for (const partner of data.partnerMaintains) {
                        if (partner.infoMaintain) {
                            for (const info of partner.infoMaintain) {
                                if (info.dataPacks && info.dataPacks.length > 0) {
                                    // Determine which API to call based on service type
                                    if (info.serviceCode === ServiceCodeMaintain.DATA_SPONSOR) {
                                        const response = await apiGetListPackageDataSponsorPartner({
                                            cpCode: partner.cpCode,
                                            ids: info.dataPacks.join(',')
                                        });
                                        if (response.data?.packages) {
                                            response.data.packages.forEach((pkg: any) => {
                                                newPackageDetailsMap[ScopeMaintain.PARTNER + '_' + ServiceCodeMaintain.DATA_SPONSOR + '_' + pkg.ID] = pkg;
                                            });
                                        }
                                    } else if (info.serviceCode === ServiceCodeMaintain.DATA_CODE) {
                                        const response = await apiGetListPackageDataCodesPartner({
                                            cpCode: partner.cpCode,
                                            ids: info.dataPacks.join(',')
                                        });
                                        if (response.data?.dataCodes) {
                                            response.data.dataCodes.forEach((pkg: any) => {
                                                newPackageDetailsMap[ScopeMaintain.PARTNER + '_' + ServiceCodeMaintain.DATA_CODE + '_' + pkg.ID] = pkg;
                                            });
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // For PACKAGE scope - fetch base packages
                if (data.scopeType === ScopeMaintain.PACKAGE && data.packageMaintains) {
                    for (const pkgMaintain of data.packageMaintains) {
                        if (pkgMaintain.dataPacks && pkgMaintain.dataPacks.length > 0) {
                            const packageIds = pkgMaintain.dataPacks.map(dp => dp.id);

                            // Determine which API to call based on service type
                            if (pkgMaintain.serviceCode === ServiceCodeMaintain.DATA_SPONSOR) {
                                const response = await apiGetListPackageDataSponsor({
                                    ids: packageIds.join(',')
                                });
                                if (response.data?.packages) {
                                    response.data.packages.forEach((pkg: any) => {
                                        newPackageDetailsMap[ScopeMaintain.PACKAGE + '_' + pkgMaintain.serviceCode + '_' + pkg.package.ID] = pkg.package;
                                    });
                                }
                            } else if (pkgMaintain.serviceCode === ServiceCodeMaintain.DATA_CODE) {
                                const response = await apiGetListPackageDataCodes({
                                    ids: packageIds.join(',')
                                });
                                if (response.data?.dataCodes) {
                                    response.data.dataCodes.forEach((pkg: any) => {
                                        newPackageDetailsMap[ScopeMaintain.PACKAGE + '_' + pkgMaintain.serviceCode + '_' + pkg.ID] = pkg;
                                    });
                                }
                            }
                        }
                    }
                }

                setPackageDetailsMap(newPackageDetailsMap);
                console.log('Fetched package details map======================:', newPackageDetailsMap);
            } catch (error) {
                console.error('Error fetching package details:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPackageDetails();
    }, [data, open]);

    if (!data) return null;

    const renderServiceMaintains = (services?: ServiceMaintain[]) => {
        if (!services || services.length === 0) return <Text type="secondary">Không có dữ liệu</Text>;

        return (
            <Space direction="vertical" style={{ width: '100%' }}>
                {services.map((service, index) => (
                    <Card key={index} size="small" style={{ marginBottom: 8 }}>
                        <div>
                            <Text strong>Dịch vụ: </Text>
                            <Tag color="blue">{serviceCodeLabels[service.serviceCode] || service.serviceCode}</Tag>
                        </div>
                        {service.providerNames && service.providerNames.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                <Text strong>Nhà cung cấp: </Text>
                                {service.providerNames.map((provider, idx) => (
                                    <Tag key={idx} color="green">{provider}</Tag>
                                ))}
                            </div>
                        )}
                    </Card>
                ))}
            </Space>
        );
    };

    const renderPartnerMaintains = (partners?: PartnerMaintain[]) => {
        if (!partners || partners.length === 0) return <Text type="secondary">Không có dữ liệu</Text>;

        return (
            <Space direction="vertical" style={{ width: '100%' }}>
                {partners.map((partner, index) => (
                    <Card key={index} size="small" style={{ marginBottom: 8 }}>
                        <div>
                            <Text strong>Đối tác: </Text>
                            <Tag color="purple">{partner.cpCode}</Tag>
                        </div>
                        {partner.infoMaintain && partner.infoMaintain.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                {partner.infoMaintain.map((info, idx) => (
                                    <div key={idx}
                                         style={{
                                             marginLeft: 16,
                                             marginTop: 4,
                                             padding: 8,
                                             border: '1px solid #d9d9d9',
                                             borderRadius: 4,
                                             backgroundColor: '#fafafa'
                                         }}

                                    >
                                        <Text>Dịch vụ: </Text>
                                        <Tag color="blue">{serviceCodeLabels[info.serviceCode] || info.serviceCode}</Tag>
                                        {info.dataPacks && info.dataPacks.length > 0 && (
                                            <div style={{ marginTop: 4 }}>
                                                <Text>Gói: </Text>
                                                {info.dataPacks.map((packId, packIdx) => {
                                                    const packageDetail = packageDetailsMap[ScopeMaintain.PARTNER + '_' + info.serviceCode + '_' + packId];
                                                    const displayName = packageDetail?.dataPack || packageDetail?.dataCode || packId;
                                                    return (
                                                        <Tag style={{ marginBottom: 4 }} key={packIdx} color="cyan">{displayName}</Tag>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                ))}
            </Space>
        );
    };

    const renderPackageMaintains = (packages?: PackageMaintain[]) => {
        if (!packages || packages.length === 0) return <Text type="secondary">Không có dữ liệu</Text>;

        return (
            <Space direction="vertical" style={{ width: '100%' }}>
                {packages.map((pkg, index) => (
                    <Card key={index} size="small" style={{ marginBottom: 8 }}>
                        <div>
                            <Text strong>Dịch vụ: </Text>
                            <Tag color="blue">{serviceCodeLabels[pkg.serviceCode] || pkg.serviceCode}</Tag>
                        </div>
                        {pkg.dataPacks && pkg.dataPacks.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                {pkg.dataPacks.map((pack, idx) => {
                                    const packageDetail = packageDetailsMap[ScopeMaintain.PACKAGE + '_' + pkg.serviceCode + '_' + pack.id];
                                    const displayName = packageDetail?.dataPack || packageDetail?.dataCode ||pack.id;
                                    return (
                                        <div
                                            key={idx}
                                            style={{
                                                marginLeft: 16,
                                                marginTop: 4,
                                                padding: 8,
                                                border: '1px solid #d9d9d9',
                                                borderRadius: 4,
                                                backgroundColor: '#fafafa'
                                            }}
                                        >
                                            <Text>Gói: </Text>
                                            <Tag color="cyan">{displayName}</Tag>
                                            {pack.providerNames && pack.providerNames.length > 0 && (
                                                <div style={{ marginTop: 4 }}>
                                                    <Text>Nhà cung cấp: </Text>
                                                    {pack.providerNames.map((provider, provIdx) => (
                                                        <Tag key={provIdx} color="green">{provider}</Tag>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Card>
                ))}
            </Space>
        );
    };

    const renderScopeDetails = () => {
        switch (data.scopeType) {
            case ScopeMaintain.GLOBAL:
                return <Text>Bảo trì toàn hệ thống - không có cấu hình bổ sung</Text>;

            case ScopeMaintain.SERVICE:
                return (
                    <div>
                        <Title level={5}>Dịch vụ bảo trì:</Title>
                        {renderServiceMaintains(data.serviceMaintains)}
                    </div>
                );

            case ScopeMaintain.PARTNER:
                return (
                    <div>
                        <Title level={5}>Đối tác bảo trì:</Title>
                        {renderPartnerMaintains(data.partnerMaintains)}
                    </div>
                );

            case ScopeMaintain.PACKAGE:
                return (
                    <div>
                        <Title level={5}>Gói bảo trì:</Title>
                        {renderPackageMaintains(data.packageMaintains)}
                    </div>
                );

            default:
                return <Text type="secondary">Không xác định</Text>;
        }
    };

    return (
        <Modal
            title="Chi tiết lịch trình bảo trì"
            open={open}
            onCancel={onClose}
            footer={null}
            width={800}
            centered
        >
            <Spin spinning={loading}>
                <Descriptions bordered column={1} size="small">
                    <Descriptions.Item label="Tên lịch trình">
                        <Text strong>{data.name}</Text>
                    </Descriptions.Item>

                    <Descriptions.Item label="Phạm vi bảo trì">
                        <Tag color="orange">
                            {mapMantenanceScope[data.scopeType] || data.scopeType}
                        </Tag>
                    </Descriptions.Item>

                    <Descriptions.Item label="Thời gian bắt đầu">
                        {data.startTime}
                    </Descriptions.Item>

                    <Descriptions.Item label="Thời gian kết thúc">
                        {data.expireTime ? formatEndTime(data.startTime, data.expireTime) : '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Thời gian bảo trì">
                        {data.expireTime ? formatUnixTime(data.expireTime) : '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Ghi chú">
                        {data.note || <Text type="secondary">Không có ghi chú</Text>}
                    </Descriptions.Item>

                    <Descriptions.Item label="Người tạo">
                        {data.createdBy}
                    </Descriptions.Item>

                    <Descriptions.Item label="Thời gian tạo">
                        {data.createdAt}
                    </Descriptions.Item>
                </Descriptions>

                <Divider />

                {renderScopeDetails()}
            </Spin>
        </Modal>
    );
}
